import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {TherapiesService} from './therapies.service';
import {
  addReviewsActionCreator,
  addTherapyToPriorityListActionCreator,
  appendClinicalRecommendationsActionCreator,
  clearClinicalRecommendationsActionCreator,
  initialUserReview,
  initReviewTherapiesActionCreator,
  removeTherapyFromPriorityListActionCreator,
  setAnyResourcesActionCreator,
  setCaseStatusActionCreator,
  setNotSentForInterpretationTherapiesActionCreator,
  setOpinionActionCreator,
  setPermittedActionsActionCreator,
  setPriorityListActionCreator,
  setProvidersTherapiesActionCreator,
  setReviewsActionCreator,
  setTherapiesActionCreator,
  setTreatmentsActionCreator,
  setTrialsActionCreator,
  setUncharacterizedTherapiesActionCreator,
  THERAPIES_ACTIONS,
  TreatmentGlobalConfigurationsAction,
  UpdateItemInPriorityAction,
  updateRationaleTherapiesActionCreator
} from '../store/therapies.actions';
import {IclinicalTrials} from '../interfaces/clinical-trials.interface';
import {Idrug} from '../interfaces/drug.interface';
import {CoreActionCreatorService} from '../../core/services/core-action-creator.service';
import {ItherapyState} from '../store/therapies.reducer';
import * as immutable from 'immutable';
import {DiagnosticOrderActionsService} from '@ispm/test/services/diagnostic-order-actions.service';
import {CastTypes} from '../../core/utils/type-casting,util';
import {WorkflowDrivenActionCreatorAbstract} from '@ispm/workflow/classes/workflow-driven-action-creator.abstract';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {getActiveRecord} from '@ispm/workflow/utils/store.utils';
import {Observable} from 'rxjs/Observable';
import {ItherapyMatch} from '../interfaces/therapy-match.interface';
import {catchError, filter, flatMap, map, mergeMap, pluck, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {ResourceRestService} from '@ispm/workflow/services/resource-rest.service';
import {Resources} from '@ispm/workflow/constants/resources.enum';
import {IResource} from '@ispm/core/interfaces/resource.interface';
import {Drug} from '@ispm/therapies/classes/Drug';
import {ITreatmentGlobalConf, IViewConfiguration} from '@ispm/therapies/interfaces/view-configuration.interface';
import {cloneDeep as _cloneDeep, get, isEmpty} from 'lodash';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {IDefinition} from "../../configuration/interfaces/definition.interface";
import {RESOURCE_TYPE_NAME} from "@ispm/therapies/constants/default-clinical-recommendation-sorting-configuration.constant";
import {throwError} from "rxjs";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import {buttonsNamesEnum} from "@ispm/test/constants/Tabs.constants";
import {ConfigurationStoreHandler} from "../../configuration/services/configuration-store-handler.service";
import {PrioritizationConfigurationAdapter} from "@ispm/therapies/classes/prioritization-configuration.adapter";

@Injectable()
export class TherapiesActionCreatorService extends WorkflowDrivenActionCreatorAbstract<ItherapyMatch> {

  constructor(
    store: Store<any>,
    workflowConfigService: WorkflowConfigService,
    private coreAC: CoreActionCreatorService,
    private diagnosticOrderActionsServiceAC: DiagnosticOrderActionsService,
    private testService: TestService,
    private therapyRequests: TherapiesService,
    private configurationHandler: ConfigurationStoreHandler,
    private prioritizationAdapter: PrioritizationConfigurationAdapter,
  ) {
    super(store, ['therapyMatch'], workflowConfigService);
    this.diagnosticOrderActionsService = diagnosticOrderActionsServiceAC;
  }

  private static META_KEY: string = '__treatment_meta';
  private static TEMP_VALUE_KEY: string = '_tempValue';
  private diagnosticOrderActionsService: DiagnosticOrderActionsService;

  public static getTherapyIdentifier(therapy, indexProperty: string = 'id') {
    if (get(therapy, indexProperty)) {
      return get(therapy, indexProperty);
    } else if (therapy.source && therapy.source === 'manual') {
      return [therapy.name, therapy.source, JSON.stringify(therapy.target), therapy.id].join('-');
    } else {
      return `${therapy.name}-${therapy.target}`;
    }
  }

  /**
   * Set hidden META key for a treatment
   * This key will not be sent to the WS
   * @param item
   * @param updateOn
   * @constructor
   */
  public static SetStoreMetaForTreatment(item: any, updateOn: string) {
    if (!item) {
      return;
    }

    Object.defineProperty(item, this.META_KEY, {
      configurable: true,
      enumerable: false,
      value: {
        updateOn
      },
      writable: true
    });
  }

  public static groupTrialsByContextType(trials: IclinicalTrials[], contextTypes: string[] = ['molecularfinding', 'aberration']): { [aberrationID: string]: IclinicalTrials[]} {
    return trials.reduce((groupedTrials, trial) => {
      const matchedContexts = trial.context.filter(context => contextTypes.indexOf(context.resourceType.toLowerCase()) > -1);
      matchedContexts.forEach(matchedContext => {
        if (!groupedTrials[matchedContext.reference]) {
          groupedTrials[matchedContext.reference] = [];
        }
        groupedTrials[matchedContext.reference].push(trial);
      });

      return groupedTrials;
    }, { });
  }

  /**
   * fetch the reviews by revision Id and set them at the state
   */
  public getReviews(): Observable<any[]> {
    return this.restoreState<any[]>('reviews',
      this.therapyRequests.getPriority()
        .pipe(
          tap(reviews => this.setReviews(reviews)),
          catchError(err => {
            this.setReviews([]);
            return throwError(err);
          }),
        ),
    ).pipe(
      // escaping nullish data caused by cleaning store
      filter(reviews => !!reviews),
    );
  }

  public getViewConfig(): Observable<IViewConfiguration> {
    return this.configurationHandler.getCurrentConfig().pipe(
      switchMap(definition =>
        this.workflowConfigService.getActiveWorkflow().pipe(
          withLatestFrom(this.workflowConfigService.getRequestedRevisionId()),
          map(([workflow, revisionId]) => ({ definition, workflow, revisionId})),
        )
      ),
      map(({ definition, workflow, revisionId}) => {
        const revision = workflow.revisions?.find(_revision => _revision.id === revisionId) || null;
        const currentJobName = revision?.activeJobPurpose || revision?.activeJobName;

        const activeJob = workflow.activeRevision === revisionId ? workflow.activeJob : null;
        const currentStep = revision?.items.find(item => item.id === activeJob) || null;
        return this.prioritizationAdapter.getViewConfig(definition, currentStep, currentJobName);
      }),
    );
  }

  private getClinicalTrialRecommendations(namedResources, definition: IDefinition): any[] /*IClinicalRecommendation[]*/ {
    if (!definition.getTrialsFromRecommendations) {
        return namedResources[RESOURCE_TYPE_NAME.TRIALS] || [];
    }

    const rawTrials = namedResources[RESOURCE_TYPE_NAME.CLINICAL_RECOMMENDATIONS]
      .filter(resource => resource.clinicalRecommendationType === 'ClinicalTrial') || [];

    return this.therapyRequests.addAberrationsFromBiomarkers(rawTrials);
  }

  private getResolversFromViewConfig(definition: IDefinition, viewConfig: IViewConfiguration,
                                     resourceQueryConditions: { [name in RESOURCE_TYPE_NAME]?: { payload?: any, query?: any, sortConditions?: any}}
  ): Observable<{ [name in RESOURCE_TYPE_NAME]?: any[] }> {

    // filter resources that need to be fetched
    resourceQueryConditions[RESOURCE_TYPE_NAME.OPINION] = { };
    resourceQueryConditions[RESOURCE_TYPE_NAME.REVIEWS] = { };
    resourceQueryConditions[RESOURCE_TYPE_NAME.TREATMENTS] = { };

    // therapies can be split to normal and 'uncharacterizedTherapies'
    const allTherapies = get(viewConfig.subject_selection_section, 'payload.allTherapies', false);
    if (allTherapies) {
      resourceQueryConditions[RESOURCE_TYPE_NAME.UNCHARACTERIZED_THERAPIES] = { };
    }

    // prepares resolvers
    const resolvers = Object.keys(resourceQueryConditions)
      .map((name: RESOURCE_TYPE_NAME) => {
        const resourceName = name === RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS && !definition.getTrialsFromRecommendations ? RESOURCE_TYPE_NAME.TRIALS : name;
        return this.getResolverByResourceName(resourceName, viewConfig, resourceQueryConditions[resourceName])
            .pipe(map(resource => ({ name: resourceName, resource})));
        }
      );

    return combineLatest(resolvers).pipe(
      map(namedResources => namedResources.reduce((accum, namedResource) =>
        ({ ...accum, [namedResource.name]: namedResource.resource}), { } as { [name in RESOURCE_TYPE_NAME]?: any})
      ),
      map( resourcesMap => {
        if (resourceQueryConditions[RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS]) {
          // RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS - is not an independent resource of type 'IclinicalTrials'
          resourcesMap[RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS] = this.getClinicalTrialRecommendations(resourcesMap, definition);
        }
        return resourcesMap;
      }),
    );
  }

  public prefetchTestResources(): Observable<{ [name in RESOURCE_TYPE_NAME]?: any[] }> {
    const definitionViewConfigAndQueryConditions$ = combineLatest([
      this.configurationHandler.getCurrentConfig(),
      this.getViewConfig(),
    ]).pipe(
      map(([definition, viewConfig]) =>
        ({ definition, viewConfig, resourceQueryConditions: this.getResourcesQueryConditions(viewConfig)}))
    );

    return definitionViewConfigAndQueryConditions$.pipe(
      mergeMap(({ definition, viewConfig, resourceQueryConditions}) =>
        this.getResolversFromViewConfig(definition, viewConfig, resourceQueryConditions),
      ),
    );
  }

  public getResourcesQueryConditions(viewConfig: IViewConfiguration): { [key in RESOURCE_TYPE_NAME]?: {
    query?: any,
    sortConditions?: any,
    payload?: any,
  }} {
    return viewConfig.information_section.tabs.reduce((accum, tab) => {
      if (tab.panels.length && tab.additionalData?.singlePanel) {
        const firstPanel = tab.panels[0];
        accum[firstPanel.resource || RESOURCE_TYPE_NAME.ANY_RESOURCES] = {
          query: firstPanel.additionalData?.query || null,
          sortConditions: firstPanel.additionalData?.additionalSortConditions || null,
          payload: firstPanel.resourcePayload || { },
        };
      } else {
        tab.panels.forEach(panel =>
          accum[panel.resource || RESOURCE_TYPE_NAME.ANY_RESOURCES] = {
            query: panel.additionalData?.query || null,
            sortConditions: panel.additionalData?.additionalSortConditions || null,
            payload: panel.resourcePayload || { },
          }
        );
      }
      return accum;
    }, { });
  }

  private getResolverByResourceName(
    resourceName: RESOURCE_TYPE_NAME,
    viewConfig: IViewConfiguration,
    resourceQueryCondition: { payload?: any, query?: any, sortConditions?: any} = { },
  ): Observable<any> /*| undefined*/ {

    switch (resourceName) {
      case RESOURCE_TYPE_NAME.TRIALS:
        return this.getTrials();

      case RESOURCE_TYPE_NAME.THERAPIES:
        const allTherapies = get(viewConfig.subject_selection_section, 'payload.allTherapies', false);
        return this.getTherapies(allTherapies);

      case RESOURCE_TYPE_NAME.UNCHARACTERIZED_THERAPIES:
        return this.getTherapies(true).pipe(
          switchMap(() => this.getState(RESOURCE_TYPE_NAME.UNCHARACTERIZED_THERAPIES))
        );

      case RESOURCE_TYPE_NAME.TREATMENTS:
        return this.getTreatments();

      case RESOURCE_TYPE_NAME.CLINICAL_RECOMMENDATIONS:
        return this.getClinicalRecommendations(resourceQueryCondition?.query, resourceQueryCondition?.sortConditions);

      case RESOURCE_TYPE_NAME.OPINION:
        return this.getOpinion();

      case RESOURCE_TYPE_NAME.REVIEWS:
        return this.getReviews();

      // case RESOURCE_TYPE_NAME.ANY_RESOURCES:
      //   return this.getAnyResources();

      default:
        return this.getAnyResources().pipe(
          map(res => {
            return res || [];
          })
        );
        // return throwError('Unknown source type name');
    }
  }

  /**
   * return the unwrapped value from the store using the cache key
   * @param props
   * @param value
   */
  public handleStateValue(props: Array<string>, value: any) {
    if (props.length > 0) {
      return super.handleStateValue(props, value);
    } else {
      return Observable.of(value);
    }
  }

  public getHiddenLevelsOfEvidence() {
    return this.testService.getHiddenLevelsOfEvidence();
    // return _.get(this.testService, "testConfiguration.therapyMatch.hiddenLevelOfEvidence", "['Not determined', 'None found']");
  }

  public checkIfTherapyUncharacterized(therapy: ItherapyMatch) {
    return therapy.type && therapy.type === 'uncurated'
      ? !this.checkIfTherapyNotSentForInterpretation(therapy)
      : false;
  }

  public checkIfTherapyNotSentForInterpretation(therapy: ItherapyMatch) {
    if (therapy['edited']) {
      return false;
    }
    else if (therapy.interpretationSkipped) {
      return true;
    }
    else {
      return !(therapy.aberration && therapy.aberration.THERAPY_AVAILABILITY);
    }
  }

  public checkIfNormalTherapyMatch(therapy: ItherapyMatch) {
    if (get(therapy, 'gene.name', '').toUpperCase() === 'TUMOR MUTATIONAL BURDEN') {
      return true;
    }
      else if (therapy.gene && !!therapy.alteration && get(therapy, 'alteration', '').toUpperCase() === 'WILD TYPE') {
      return true;
    }
    else {
      return !this.checkIfTherapyUncharacterized(therapy) && !this.checkIfTherapyNotSentForInterpretation(therapy);
    }
  }

  public getOpinion(): Observable<any[]> {
    return this.restoreState<any[]>('opinion',
      this.therapyRequests.getOpinion()
        .pipe(
          tap((opinion) => this.setOpinion(opinion)),
          catchError(err => {
            this.setOpinion([]);
            return throwError(err);
          }),
        ),
    ).pipe(
      // escaping nullish data caused by cleaning store
      filter(opinion => !!opinion),
    );
  }

  public setOpinion(opinion: any) {
    this.store.dispatch(setOpinionActionCreator(opinion));
  }

  /**
   * fetch therapies by revision Id and set them at the state
   * @param {boolean} allTherapies - return all therapies type from entity tree
   */
  public getTherapies(allTherapies: boolean = false): Observable<any[]> {
    return this.restoreState<any[]>('therapies',
        this.therapyRequests.getTherapies(allTherapies).pipe(
          map((therapies: Array<ItherapyMatch>) => {
            const providers = new Set();
            therapies.forEach(therapy => {
              if (therapy.drugs) {
                therapy.drugs.forEach(drug => {
                  this.setDrugDisplayName(drug, this.getHiddenLevelsOfEvidence());
                });
              }

              const provider = get(therapy, 'provider', '').toLowerCase();

              if (provider.includes('nof1') || provider.includes('nofone') ) {
                providers.add('Powered by N-of-One®');
              }
            });
            this.setProviders(Array.from<any>(providers));
            // If all therapies are configured to be displayed, then separate them into 3 categories
            //            1. Therapy Matches Supported For Interpretation which are either Actionable or Inactionable
            //                OR Therapy Matches which are listed as 'uncurated'
            //            3. Therapy Matches which are not supported for intepretation--denoted by field 'THERAPY_AVAILABILITY'
            if (allTherapies) {
              this.setUncharacterizedTherapies(therapies.filter((therapy) => {
                return this.checkIfTherapyNotSentForInterpretation(therapy) || therapy.type === 'uncurated'; // || this.checkIfTherapyUncharacterized(therapy);
              }));

              this.setTherapies(therapies.filter((therapy) => {
                return this.checkIfNormalTherapyMatch(therapy);
              }));
            } else {
              this.setTherapies(therapies);
            }
          }),
          catchError(err => {
            this.setTherapies([]);
            return throwError(err);
          }),
        )
      ).pipe(
        // escaping nullish data caused by cleaning store
        filter(therapies => !!therapies),
      );
  }

  public getAnyResources() {
    return this.getState('anyResources');
  }

  public getPermittedActions() {
    return this.getState('permittedActions');
  }

  public clearClinicalRecommendations() {
    this.store.dispatch(clearClinicalRecommendationsActionCreator(null));
  }

  public getClinicalRecommendations(sourceQuery?: any, sortConditions?: any): Observable<any[]> {
    return this.restoreState<any[]>('clinicalrecommendations',
      this.therapyRequests.getClinicalRecommendations(sourceQuery, sortConditions)
        .pipe(
          tap((clinicalrecommendations) => this.store.dispatch(appendClinicalRecommendationsActionCreator(clinicalrecommendations))),
          catchError(err => {
            this.store.dispatch(appendClinicalRecommendationsActionCreator([]));
            return throwError(err);
          }),
        ),
    ).pipe(
      // escaping nullish data caused by cleaning store
      filter(clinicalrecommendations => !!clinicalrecommendations),
    );
  }

  /**
   * resolve all treatments by revision
   * */
  public getTreatments(): Observable<any[]> {
    return this.restoreState<any[]>('treatments',
      this.therapyRequests.getTreatments()
        .pipe(
          tap((treatments) => this.setTreatments(treatments)),
          catchError(err => {
            this.setTreatments([]);
            return throwError(err);
          }),
        ),
    ).pipe(
      // escaping nullish data caused by cleaning store
      filter(treatments => !!treatments),
    );
  }

  public setTherapyDrugsDisplayNames(therapies: any[] | null, hiddenLevelsOfEvidence: any) {
    therapies?.forEach(therapy => {
      if (therapy.drugs) {
        therapy.drugs.forEach(drug => this.setDrugDisplayName(drug, hiddenLevelsOfEvidence));
      }
    });
  }

  public setDrugDisplayName(drug: Idrug, hiddenLevelsOfEvidence: any) {
    let drugName;

    if (drug.therapeuticLevelOfEvidence === undefined
      || drug.therapeuticLevelOfEvidence === null
      || hiddenLevelsOfEvidence.includes(drug.therapeuticLevelOfEvidence)
    ) {
      drugName = drug.name;
    } else {
      drugName = drug.name + ' (' + drug.therapeuticLevelOfEvidence + ')';
    }

    drug.displayName = drugName;
  }

  /**
   * add a therapy to the existing list.
   * @param {Idrug| IResource} treatment
   * @param {string} indexProperty
   * @param {boolean} isOffPathway
   */
  public addTherapy(treatment: Idrug | IResource, indexProperty: string = 'id', isOffPathway = false) {
    this.getTreatmentGlobalConfiguration().pipe(
      take(1),
      mergeMap((configuration: immutable.Map<string, any>) => {
        return this.getUserReview()
          .pipe(
            map(review => {
              return {
                data: review,
                limit: configuration.get('maxSelections')
              };
        }));
      })
    ).subscribe(({ data, limit}) => {
      // TODO remove when offPathway is enhanced to support multiple selection
      if (isOffPathway) {
        limit = 1;
      }
      // Get temp value index to extend existing data
      const index = data.findIndex((item: IResource) => item.id === TherapiesActionCreatorService.TEMP_VALUE_KEY);

      if (index > -1) {
        const tempData = data.get(index);
        delete tempData['id'];
        const originalTreatment = treatment;
        treatment = Object.assign({ }, treatment, tempData);

        if (originalTreatment[TherapiesActionCreatorService.META_KEY]) {
          TherapiesActionCreatorService.SetStoreMetaForTreatment(
            treatment, originalTreatment[TherapiesActionCreatorService.META_KEY].updateOn
          );
        }

        data = data.delete(index);
      }

      const treatmentIndex = data.findIndex((item: IResource) => item.id === treatment.id);

      if (data.size + 1 > limit && limit != -1 && treatmentIndex == -1) {
        if (limit === 1) {
          // in case limitation is one toggle the selection (remove previous add new)
          data = (<immutable.List<IResource>> data).clear();
        } else {
          // reach to the limit, don't apply change
          return;
        }
      }

      if (!(<IResource> treatment).resourceType) {
        this.setDrugDisplayName(treatment as Drug, this.getHiddenLevelsOfEvidence());
      }

      if (limit) {

        if (treatmentIndex > -1) {
          if (data.get(treatmentIndex) !== treatment) {
            data = data.delete(treatmentIndex);
            data = data.push(treatment);
          }
           else {
            data = data.delete(treatmentIndex);
          }
        } else {
          data = data.push(treatment);
        }

        if (isOffPathway && data.size > 1) {
          const offPathIndex = data.findIndex((item: IResource) => item.id === 'offPathway');

          if ( offPathIndex > -1 ) {
            data = data.delete(offPathIndex);
          }
        }
      } else {
        data = data.push(treatment);
      }

      this.setPriorityList(data, indexProperty);
    });
  }

  /**
   * Update stored values by the updateOn key which set via SetStoreMetaForTreatment
   * @link SetStoreMetaForTreatment
   * @param updateOnKey
   * @param updateValue
   * @param indexProperty
   */
  public updateStoredTherapies(updateOnKey: string, updateValue: any, indexProperty: string) {
    this.getUserReview()
      .pipe(take(1))
      .subscribe((data: immutable.List<Idrug | IResource>) => {
        let found = false;
        let tempItemIndex = data.findIndex(item => item[TherapiesActionCreatorService.TEMP_VALUE_KEY]);

        if (tempItemIndex !== -1) {
          data = data.delete(tempItemIndex);
          tempItemIndex = -1;
        }

        if (tempItemIndex === -1 && data.size === 0) {
          const tempObject = {
            id: TherapiesActionCreatorService.TEMP_VALUE_KEY
          };
          Object.defineProperty(
            tempObject,
            TherapiesActionCreatorService.TEMP_VALUE_KEY,
            {value: true, enumerable: false, writable: true, configurable: true}
          );

          return this.setPriorityList([Object.assign(tempObject, updateValue)]);
        }

        data.forEach((treatment) => {
          if (
            treatment[TherapiesActionCreatorService.META_KEY] &&
            treatment[TherapiesActionCreatorService.META_KEY].updateOn === updateOnKey
          ) {
            found = true;
            Object.assign(treatment, updateValue);
          }
        });

        if (found) {
          this.setPriorityList(data, indexProperty);
        }
      });
  }

  /**
   * fetch trials by revision Id, set them the by Aberration Id or Molecular Finding Id and store it on the state
   */
  public getTrials(): Observable<IclinicalTrials[]> {
    return this.restoreState<IclinicalTrials[]>('trials',
      this.therapyRequests.getTrialsWithAberrationsAsAggregateChildren()
        .pipe(
          map((trials: IclinicalTrials[]) => {
            this.setTrials(trials);
          }),
          catchError(err => {
            this.setTrials([]);
            return throwError(err);
          }),
        )
    ).pipe(
      // escaping nullish data caused by cleaning store
      filter(trials => !!trials),
    );
  }

  /**
   * set therapies at the state
   * @param therapies
   */
  public setTherapies(therapies: Array<ItherapyMatch> | any) {
    this.store.dispatch(setTherapiesActionCreator(therapies));
  }

  /**
   * set uncharacterized therapies at the state
   * @param uncharacterizedTherapies
   */
  public setUncharacterizedTherapies(uncharacterizedTherapies: any) {
    this.store.dispatch(setUncharacterizedTherapiesActionCreator(uncharacterizedTherapies));
  }

  /**
   * set therapies which are unavailable for interpretation
   * @param notSentForInterpretationTherapies
   */
  public setNotSentForInterpretationTherapies(notSentForInterpretationTherapies: any) {
    this.store.dispatch(setNotSentForInterpretationTherapiesActionCreator(notSentForInterpretationTherapies));
  }

  /**
   * set trials at the state
   * @param trials
   */
  public setTrials(trials) {
    this.store.dispatch(setTrialsActionCreator(trials));
  }

  /**
   * set treatments at the state
   * @param treatments
   */
  public setTreatments(treatments) {
    this.store.dispatch(setTreatmentsActionCreator(treatments));
  }

  public setAnyResources(resources) {
    this.store.dispatch(setAnyResourcesActionCreator(resources));
  }

  public setPermittedActions(actions) {
    this.store.dispatch(setPermittedActionsActionCreator(actions));
  }

  /**
   * clear the review priority and review on the state
   */
  public setUserReviewToInitial() {
    this.store.dispatch(initReviewTherapiesActionCreator(null));
  }

  /**
   * update the rationale
   * @param rationale
   */
  public updateRationale(rationale) {
    this.store.dispatch(updateRationaleTherapiesActionCreator(rationale));
  }

  public removeTherapyFromPriority(therapy, indexProperty: string = 'id') {
    this.store.dispatch(
      removeTherapyFromPriorityListActionCreator({
        id: TherapiesActionCreatorService.getTherapyIdentifier(therapy, indexProperty),
        indexProperty: indexProperty
      })
    );
  }

  public setReviews(reviews: any[] | null) {
    this.setTherapyDrugsDisplayNames(reviews, this.getHiddenLevelsOfEvidence());
    this.store.dispatch(setReviewsActionCreator(reviews));
  }

  public addReviews(reviews: any[]) {
    this.store.dispatch(addReviewsActionCreator(reviews));
  }

  /***
   * adding therapy to priority list by appending it or by splice it by index
   * @param {Idrug} therapy
   * @param {number} index
   */
  public setTherapyInPriority(therapy: Idrug, index: number = null) {
    const identifier = TherapiesActionCreatorService.getTherapyIdentifier(therapy);
    this.getState('userReview').pipe(
        take(1),
        filter(state => !!state),
        pluck('therapiesIndex')
      )
      .subscribe(data => {
        this.setDrugDisplayName(therapy, this.getHiddenLevelsOfEvidence());

        if (data.has(identifier)) {
          this.removeTherapyFromPriority(therapy);
        } else {
          this.store.dispatch(addTherapyToPriorityListActionCreator({
            therapy,
            identifier,
            index
          }));
        }
      });
  }

  public setPriorityList(list, indexProperty: string = 'id') {
    this.store.dispatch(setPriorityListActionCreator({ list, indexProperty }));
  }

  /**
   * send the priority with the rationale to the server
   * @param {boolean} update - is a review update
   */
  public sendPriority(update: boolean = false) {
    return this.getState('userReview')
      .pipe(
        take(1),
        mergeMap((userReviewObject: any) => {
          const sendObject = {
            rationale: userReviewObject.rationale,
            drugs: Array.from(userReviewObject.priority.values()),
            revisionId: ResourceRestService.getResourcesString(Resources.RequestedRevision),
            workflowId: ResourceRestService.getResourcesString(Resources.WorkflowRequest)
          };

          return update
            ? this.therapyRequests.updatePriority(sendObject)
            : this.therapyRequests.savePriority(sendObject);
        }),
        map((data: any) => {
          if (data.workflowRequest) {
            this.workflowConfigService.setActiveWorkflow(data.workflowRequest);
          }
          return data;
        }),
        map((data: any) => {
          data.reviews = <any> data.reviews instanceof Array ? data.reviews : [data.reviews];
          this.setReviews(data.reviews);

          return data;
        })
      );
  }

  public getTreatmentGlobalConfiguration() {
    return this.store.pipe(
      pluck('globals')
    );
  }

  public getIsOverLimitationStream() {
    return combineLatest(
      this.getSelections().pipe(
        map((selections: immutable.Set<string>) => selections.delete(TherapiesActionCreatorService.TEMP_VALUE_KEY).size)
      ),
      this.getTreatmentGlobalConfiguration().pipe(
        map((globals: Map<string, any>) => globals.has('maxSelections') ? globals.get('maxSelections') : -1)
      )
    ).pipe(
      map(([selectedAmount, limitation]) => {
        if (limitation === 1) {
          return false; // allow toggling
        }

        return limitation !== -1 && selectedAmount >= limitation;
      })
    );
  }

  /**
   * toggle on accept case and set clone of priority list
   * @param practitioner
   * @param isAccept
   * @param revisionId
   * @param workflowId
   */
  public setAcceptCase(practitioner, isAccept, revisionId, workflowId) {
    this.coreAC.setLoader(true);
    this.therapyRequests.signCase(revisionId, workflowId, practitioner)
      .then(res => {
        if (res) {
          this.acceptCaseUIHandler(isAccept, revisionId);
        }

        this.coreAC.setLoader(false);
      });
  }

  public acceptCaseUIHandler(isAccept: boolean, revisionId: string) {
    const buttonStatus = isAccept
      ? buttonsNamesEnum.UNASSIGN_CASE
      : buttonsNamesEnum.ACCEPT_CASE;

    if (isAccept) {
      this.initUserReviewState(revisionId);
    }

    this.store.dispatch(setCaseStatusActionCreator(isAccept));
    this.testService.updateButtonState(buttonStatus);
  }

  public initUserReviewState(revisionId: string, force: boolean = false): Promise<{ drugs: immutable.List<any>, rationale: string }> {
    let state;
    let reviews;
    let drugs = immutable.List();
    let rationale = '';
    let userReview;

    return this.getState().take(1)
      .flatMap((data: ItherapyState) => {
        state = data || [];
        return getActiveRecord(data.userReview);
      })
      .map(userReview => {
        if (!userReview) {
          this.setUserReviewToInitial();

          return initialUserReview;
        } else {
          return userReview;
        }
      })
      .flatMap(activeUserReview => {
        userReview = activeUserReview;

        return getActiveRecord(state.reviews, null, CastTypes.ARRAY);
      })
      .map(activeReviews => {
        reviews = activeReviews || [];

        if ((userReview.priority.size > 0 || userReview.rationale != '') && !force) {
          return { drugs: userReview.priority, rationale: userReview.rationale};
        } else if (reviews.length > 0) {
          const lastReview = reviews[reviews.length - 1];

          if (lastReview.drugs) {
            drugs = lastReview.drugs;
          } else if (lastReview.priority) {
            drugs = lastReview.priority;
          }

          rationale = lastReview.rationale;
        }

        this.setPriorityList(drugs);
        this.updateRationale(rationale);

        return ({ drugs, rationale});
      })
      .toPromise();
  }

  public updateSignOffStatus(show) {
    this.testService.updateButtonState(
      show ? buttonsNamesEnum.SIGN_OFF : buttonsNamesEnum.SIGN_OFF_HIDE, 'signoff'
    );
  }

  public updateTherapies(...therapies: Array<ItherapyMatch>) {
    const updateObject = new Map();
    const therapiesList = [];
    const uncharacterizedTherapiesList = [];

    therapies.forEach(therapy => {
      if (therapy.drugs) {
        therapy.drugs.forEach(drug => {
          this.setDrugDisplayName(drug, this.getHiddenLevelsOfEvidence());
        });
      }

      if (this.checkIfTherapyUncharacterized(therapy) || this.checkIfTherapyNotSentForInterpretation(therapy)) {
        uncharacterizedTherapiesList.push(therapy);
      }
      else if (this.checkIfNormalTherapyMatch(therapy)) {
        therapiesList.push(therapy);
      }
    });

    updateObject.set('therapies', therapiesList);
    updateObject.set('uncharacterizedTherapies', uncharacterizedTherapiesList);

    const results = Array.from(updateObject.keys()).map(therapyType => {
      return this.getState(therapyType)
        .pipe(
          take(1),
          map((storeTherapies: Array<ItherapyMatch>) => {
            if (!storeTherapies) {
              return [];
            }

            const updatedTherapies = updateObject.get(therapyType);

            if (updatedTherapies) {
              therapies.forEach(therapy => {
                const matchIndex = storeTherapies.findIndex(storeTherapy => storeTherapy.id === therapy.id);
                const updatedTherapy = updatedTherapies.find(storeTherapy => storeTherapy.id === therapy.id);

                if (updatedTherapy) {
                  if (matchIndex > -1) {
                    storeTherapies.splice(matchIndex, 1, updatedTherapy); // replacing existing therapy on update
                  }
                  else {
                    storeTherapies.unshift(updatedTherapy);
                  }
                }
                else {
                  if (matchIndex > -1) { // In case therapy was moved from one list to another (e.g. from uncurated to actionable), remove it from the previous list
                    storeTherapies.splice(matchIndex, 1);
                  }
                }
              });
            }
            // storeTherapies.splice(0,0,...updateObject.get(therapyType));

            return [...storeTherapies];
          }),
          map((storeTherapies: Array<ItherapyMatch>) => {
            switch (therapyType) {
              case 'uncharacterizedTherapies':
                this.setUncharacterizedTherapies(storeTherapies);
                break;

              default:
                this.setTherapies(storeTherapies);
                break;
            }

            return { therapyType, therapies: storeTherapies};
          })
        );
    });
    return forkJoin(results).pipe(
      map(storeResult => {
        const result = { };
        storeResult.forEach((item: any) => {
          results[item.therapyType] = item.therapies;
        });

        return result;
      })
    );
  }

  public updateInclusionOpinion(includeIn: 'report' | 'MI', updateId, includeInReport) {
    return this.toggleOnIncludeLoader(includeIn, updateId).pipe(
      switchMap(() => this.therapyRequests.updateVisibilityOpinion(includeIn, updateId, includeInReport)),
      tap(opinion => this.setOpinion([opinion]))
    );
  }

  public toggleOnIncludeLoader(includeIn: 'report' | 'MI', id: string) {
    return this.getOpinion().pipe(
      take(1),
      map((opinions: any[]) => {
        const opinion = opinions[0];

        if (includeIn === 'report') {
          opinion.includeInReport = opinion.includeInReport || { [id]: { }};
          opinion.includeInReport[id] = opinion.includeInReport[id] || { };
          Object.assign(opinion.includeInReport[id], { visible: null});
        }
        else {
          opinion.includeInMI = opinion.includeInMI || { [id]: { }};
          opinion.includeInMI[id] = opinion.includeInMI[id] || { };
          Object.assign(opinion.includeInMI[id], { visible: null});
        }
        this.setOpinion(opinions);
        return opinions;
      })
    );
  }

  /**
   * init the state
   */
  public initTherapyState() {
    this.store.dispatch({
      type: THERAPIES_ACTIONS.INIT_THERAPY_STATE,
    });
  }

  updateTherapyMatch(therapy: ItherapyMatch, testId: string) {
    return this.therapyRequests.updateTherapyMatch(therapy, testId).pipe(
      flatMap((therapy) => {
        return this.updateTherapies(therapy).pipe(
          map((therapies: { [type: string]: ItherapyMatch}) => therapy)
        );
      })
    );
  }

  public updateClinicalRecommendation(clinicalRecommendation: any, testId: string) {
    return this.therapyRequests.updateClinicalRecommendation(this.sanitizeRecommendation(clinicalRecommendation), testId)
      .pipe(
        map(updatedRecommendation => {
          updatedRecommendation['_aggregatedChildren'] = clinicalRecommendation['_aggregatedChildren'];

          return updatedRecommendation;
        })
      );
  }

  private sanitizeRecommendation(recommendation) {
    const cleanRecommendation = _cloneDeep(recommendation);
    delete cleanRecommendation._aggregatedChildren;
    delete cleanRecommendation._references;

    return cleanRecommendation;
  }

  public getSelections() {
    return this.getState('userReview').pipe(
      filter(state => !!state),
      pluck('therapiesIndex')
    );
  }

  public getPriorityList() {
    return this.getState('userReview').pipe(
      filter(state => !!state),
      pluck('priority')
    );
  }

  public getIsSelectedStream(therapyIdentifier: string): Observable<boolean> {
    return this.getSelections()
      .pipe(
        map((selectedTherapiesSet: Set<string>) => {
          return selectedTherapiesSet && selectedTherapiesSet.has(therapyIdentifier);
        })
      );
  }

  public getIncludedMap(): Observable<{ [includeIn in 'report' | 'MI']: { [prop: string]: { visible: boolean } } }> {
    return this.getState('opinion')
      .pipe(
        filter(opinions => !isEmpty(opinions)),
        map((opinions) => ({
          'report': opinions.find(opinion => opinion.includeInReport)?.includeInReport || { },
          'MI': opinions.find(opinion => opinion.includeInMI)?.includeInMI || { },
        }))
      );
  }

  setGlobalConfigurations(globals: Partial<ITreatmentGlobalConf>) {
    this.dispatch(new TreatmentGlobalConfigurationsAction(globals));
  }

  updateUserReview(index, value) {
    this.store.dispatch(new UpdateItemInPriorityAction(index, value));
  }

  getUserReview() {
    return this.getState('userReview')
      .pipe(
        take(1),
        filter(userReview => !!userReview),
        pluck('priority')
      );
  }

  getUserReviewFull() {
    return this.getState('userReview').pipe(take(1));
  }

  private setProviders(providers: string[]) {
    this.store.dispatch(setProvidersTherapiesActionCreator(providers));
  }
}
